import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { enrollmentMachine } from '~/machines/enrollment-selection';
import { requiresAffirmativeConsent } from '~/machines/solar-funnel/machine';
import { uploadMachine } from '~/machines/upload';
import { userMachine } from '~/machines/user-creation';
import { utilityMachine } from '~/machines/utility-selection';
import { LDClient, LDFlags } from '~/providers/launch-darkly';
import { updateUtilityAccountDetail } from '~/services/update-utility-account-detail';
import { identify } from '~/utils/tracking';
import { PerchEnergyContext, PerchEnergyParams } from './types';

export const launchDarklyIdentify = (context: PerchEnergyContext): Promise<LDFlagSet> => {
  const packet = context.funnelPacket || context.queryParams?.resumingUser?.packet;

  // Always return a Promise that resolves to an LDFlagSet
  try {
    let identifyPromise;
    if (packet) {
      identifyPromise = identify({
        email: packet.user.email,
        id: packet.user.id,
        utilityId: packet.utility.id,
        promoCode: packet.user?.promoCode,
        funnelTrack: packet.utility.funnelTrack,
        state: packet.utility.state.abbreviation,
      });
    } else {
      identifyPromise = identify({
        utilityId: context.utilitySelection?.id,
        promoCode: context.queryParams?.promoCode,
        funnelTrack: context.utilitySelection?.funnelTrack,
      });
    }

    // Ensure we always return a Promise<LDFlagSet>
    return identifyPromise || Promise.resolve({} as LDFlagSet);
  } catch (error) {
    // Make sure we always return a Promise<LDFlagSet> even if identify fails
    console.error('Error in launchDarklyIdentify:', error);
    return Promise.resolve({} as LDFlagSet);
  }
};

export const updateRequireAffirmativeConsent = async (context: PerchEnergyContext) => {
  const response = await updateUtilityAccountDetail({
    utilityAccountId: context.funnelPacket!.utilityAccount.id,
    requiresAffirmativeConsent: requiresAffirmativeConsent(context),
  });
  return {
    utilityAccountDetailId: response?.utilityAccountDetailId,
  };
};

export const perchMachineServices = (params: PerchEnergyParams) => ({
  launchDarklyIdentify,
  utilityMachine: utilityMachine(),
  userMachine: userMachine(),
  enrollmentMachine: enrollmentMachine().withConfig({
    services: {
      ...(params.enrollment?.services?.fetchBillingExperienceOptions
        ? {
            fetchBillingExperienceOptions:
              params.enrollment?.services?.fetchBillingExperienceOptions,
          }
        : {}),
    },
    guards: {
      shouldAdvance: context => {
        return ['verified'].includes(context.utilityConnectStatus!)
          ? true
          : !(
              LDClient.variation(LDFlags.accountNumberBackup) ||
              LDClient.variation(LDFlags.billImageUpload)
            ) && ['timed_out', 'error', 'rejected'].includes(context.utilityConnectStatus!);
      },
      shouldFallBackToAccountNumber: context =>
        LDClient.variation(LDFlags.accountNumberBackup) &&
        ['error', 'timed_out'].includes(context.utilityConnectStatus!),
      shouldFallBackToBillImageUpload: _context => {
        return false;
      },
    },
  }),
  updateRequireAffirmativeConsent,
  uploadMachine: uploadMachine(),
});
